import type Phaser from "phaser";

export type ViewportSize = { width: number; height: number };

const APP_ID = "app";

/**
 * Prefer visualViewport (mobile URL bar / WebView), else window.inner*.
 * Rounded to whole CSS px so Phaser FIT + SafeFrame stay stable.
 */
export function readViewportSize(
  win: Window = window,
): ViewportSize {
  const vv = win.visualViewport;
  if (vv && vv.width > 0 && vv.height > 0) {
    return {
      width: Math.max(1, Math.round(vv.width)),
      height: Math.max(1, Math.round(vv.height)),
    };
  }
  return {
    width: Math.max(1, Math.round(win.innerWidth || 1)),
    height: Math.max(1, Math.round(win.innerHeight || 1)),
  };
}

export function sizesEqual(a: ViewportSize, b: ViewportSize): boolean {
  return a.width === b.width && a.height === b.height;
}

/**
 * Pin host boxes to explicit px. Percentage `height:100%` often stays on the
 * pre-rotation size after orientationchange (Safari / Android WebView).
 */
export function applyHostViewportSize(
  size: ViewportSize,
  doc: Document = document,
): void {
  const w = `${size.width}px`;
  const h = `${size.height}px`;
  const root = doc.documentElement;
  const body = doc.body;
  root.style.width = w;
  root.style.height = h;
  if (body) {
    body.style.width = w;
    body.style.height = h;
  }
  const app = doc.getElementById(APP_ID);
  if (app) {
    app.style.width = w;
    app.style.height = h;
  }
}

export type ViewportScaleHandle = {
  /** Idempotent: re-read viewport, pin host, refresh Phaser FIT. */
  refresh: () => void;
  destroy: () => void;
};

export type AttachViewportScaleOptions = {
  /** Debounce for bursty resize / visualViewport events. Default 50ms. */
  debounceMs?: number;
  /** Extra pass after orientationchange (browsers report stale size first). */
  orientationFollowUpMs?: number;
  onAfterRefresh?: () => void;
};

/**
 * Keep Phaser Scale.FIT + DOM host in sync across rotate / resize / visualViewport.
 * Call once after Game create; destroy with UI layout teardown.
 */
export function attachViewportScale(
  game: Phaser.Game,
  opts: AttachViewportScaleOptions = {},
): ViewportScaleHandle {
  const debounceMs = opts.debounceMs ?? 50;
  const orientationFollowUpMs = opts.orientationFollowUpMs ?? 250;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let followUpTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const run = (): void => {
    if (disposed) return;
    const size = readViewportSize();
    applyHostViewportSize(size);
    // Always refresh — FIT can stick even when width×height match (letterbox CSS).
    if (game.scale) {
      game.scale.refresh();
    }
    opts.onAfterRefresh?.();
  };

  const schedule = (delay: number): void => {
    if (disposed) return;
    if (debounceTimer != null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      run();
    }, delay);
  };

  const onResize = (): void => schedule(debounceMs);

  const onOrientation = (): void => {
    schedule(debounceMs);
    if (followUpTimer != null) clearTimeout(followUpTimer);
    followUpTimer = setTimeout(() => {
      followUpTimer = null;
      schedule(0);
    }, orientationFollowUpMs);
  };

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onOrientation);

  const vv = window.visualViewport;
  vv?.addEventListener("resize", onResize);
  vv?.addEventListener("scroll", onResize);

  const gameHost = document.getElementById("game");
  let ro: ResizeObserver | null = null;
  if (gameHost && typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => schedule(debounceMs));
    ro.observe(gameHost);
  }

  run();

  return {
    refresh: () => {
      if (disposed) return;
      run();
    },
    destroy: () => {
      if (disposed) return;
      disposed = true;
      if (debounceTimer != null) clearTimeout(debounceTimer);
      if (followUpTimer != null) clearTimeout(followUpTimer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
      vv?.removeEventListener("resize", onResize);
      vv?.removeEventListener("scroll", onResize);
      ro?.disconnect();
    },
  };
}
